import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faRobot, faSpinner, faExclamationTriangle, faBug, faBolt } from '@fortawesome/free-solid-svg-icons';
import { useUser } from '@/contexts/UserContext';
import { VideoChatAPI } from '@/services/video-chat-api.routes';
import ChatMessage from '@/components/chat/ChatMessage/ChatMessage';
import GeneratingResponseBubble from '@/components/chat/GeneratingResponseBubble';
import type { ChatMessage as ChatMessageType, LLMLevel } from '@/models/chat-message';
import type { ChatSession } from '@/models';

// No conversion needed anymore - using regular ChatMessage types

interface VideoChatPanelProps {
  videoId: string;
  videoTitle: string;
}

interface SessionMessagesResult {
  session: ChatSession;
  messages: ChatMessageType[];
}

function normalizeMessages(messages: ChatMessageType[]): ChatMessageType[] {
  return messages.filter((message): message is ChatMessageType => message !== null && message.id !== undefined);
}

function isSessionNotFoundError(error: unknown): boolean {
  if (error instanceof Error === false) {
    return false;
  }

  const message = error.message.toLowerCase();
  if (message.includes('chat session not found')) {
    return true;
  }
  if (message.includes('failed to find chat session')) {
    return true;
  }
  if (message.includes('entity_not_found')) {
    return true;
  }
  return message.includes('http 404');
}

const VideoChatPanel: React.FC<VideoChatPanelProps> = ({ videoId }) => {
  const { isAdmin } = useUser();
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [input, setInput] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [loading, setLoading] = useState(true); // Start with loading to check for existing session
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatSession, setChatSession] = useState<ChatSession | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [debugMode, setDebugMode] = useState(false);
  const [isRefreshingDebug, setIsRefreshingDebug] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<ChatMessageType[]>(messages);

  const resetInvalidSessionState = useCallback((showUserMessage: boolean) => {
    setChatSession(null);
    setMessages([]);
    setChatStarted(false);
    if (showUserMessage) {
      setError('Der vorherige Chat ist nicht mehr verfügbar. Bitte starten Sie einen neuen Chat.');
    }
  }, []);

  const loadMessagesForSession = useCallback(async (
    session: ChatSession,
    allowRecovery: boolean,
    currentVideoId: string,
    currentDebugMode: boolean
  ): Promise<SessionMessagesResult | null> => {
    try {
      const messagesData = await VideoChatAPI.listMessages(session.id, undefined, false, currentDebugMode);
      const nextSession = messagesData.chatSession ?? session;

      return {
        session: nextSession,
        messages: normalizeMessages(messagesData.messages)
      };
    } catch (error) {
      if (isSessionNotFoundError(error) === false) {
        throw error;
      }

      if (allowRecovery === false) {
        resetInvalidSessionState(true);
        return null;
      }

      try {
        const recoveredSession = await VideoChatAPI.getOrCreateSession(currentVideoId, true);
        const recoveredMessages = await VideoChatAPI.listMessages(recoveredSession.id, undefined, false, currentDebugMode);
        const nextRecoveredSession = recoveredMessages.chatSession ?? recoveredSession;

        return {
          session: nextRecoveredSession,
          messages: normalizeMessages(recoveredMessages.messages)
        };
      } catch (recoveryError) {
        if (isSessionNotFoundError(recoveryError)) {
          resetInvalidSessionState(true);
          return null;
        }
        throw recoveryError;
      }
    }
  }, [resetInvalidSessionState]);

  // Keep messagesRef current
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Check for existing session on component mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        setLoading(true);
        setError(null);
        setChatSession(null);
        setMessages([]);
        setChatStarted(false);

        // Create or get existing video chat session
        const session = await VideoChatAPI.getOrCreateSession(videoId);
        const loadedSession = await loadMessagesForSession(session, true, videoId, false);
        if (loadedSession === null) {
          return;
        }

        setChatSession(loadedSession.session);
        setMessages(loadedSession.messages);

        // Only auto-start chat if session is active (not closed) AND has messages
        if (loadedSession.session.closed === false && loadedSession.messages.length > 0) {
          setChatStarted(true);
        }
      } catch (err) {
        console.error('Failed to check existing session:', err);
        if (isSessionNotFoundError(err)) {
          resetInvalidSessionState(true);
          return;
        }
        // If it's a server error (500), show user-friendly message
        if (err && typeof err === 'object' && 'message' in err) {
          const errorMessage = (err as Error).message;
          if (errorMessage.includes('500') || errorMessage.includes('Panic')) {
            setError('Video-Chat ist momentan nicht verfügbar. Das Video kann trotzdem normal angeschaut werden.');
          }
        }
        // For other errors, let user try to start fresh chat
      } finally {
        setLoading(false);
      }
    };

    checkExistingSession();
  }, [videoId, loadMessagesForSession, resetInvalidSessionState]);

  // Immediately refresh messages when debug mode changes
  useEffect(() => {
    if (chatSession === null || chatSession.id === '') return;
    const currentSessionId = chatSession.id;

    const refreshMessages = async () => {
      setIsRefreshingDebug(true);
      try {
        const messagesData = await VideoChatAPI.listMessages(currentSessionId, undefined, false, debugMode);
        setMessages(normalizeMessages(messagesData.messages));

        if (messagesData.chatSession !== undefined) {
          setChatSession(messagesData.chatSession);
        }
      } catch (err) {
        if (isSessionNotFoundError(err)) {
          resetInvalidSessionState(true);
          return;
        }
        console.error('Failed to refresh messages for debug mode:', err);
      } finally {
        setIsRefreshingDebug(false);
      }
    };

    refreshMessages();
  }, [chatSession?.id, debugMode, resetInvalidSessionState]);

  // Initialize chat session when user clicks start button
  const initializeVideoChat = async () => {
    try {
      setLoading(true);
      setError(null);

      let session: ChatSession | null = chatSession;

      // If session is closed, create a completely new session using forceNew
      if (session !== null && session.closed) {
        session = await VideoChatAPI.getOrCreateSession(videoId, true); // forceNew = true
      }
      // If no session exists, get or create one
      else if (session === null) {
        session = await VideoChatAPI.getOrCreateSession(videoId);
      }

      if (session === null) {
        setError('Fehler beim Initialisieren des Video-Chats');
        return;
      }

      const loadedSession = await loadMessagesForSession(session, false, videoId, debugMode);
      if (loadedSession === null) {
        return;
      }

      setChatSession(loadedSession.session);
      setMessages(loadedSession.messages);
      setChatStarted(true);

    } catch (err) {
      console.error('Failed to initialize video chat:', err);
      if (isSessionNotFoundError(err)) {
        resetInvalidSessionState(true);
        return;
      }
      // If it's a server error (500), show user-friendly message
      if (err && typeof err === 'object' && 'message' in err) {
        const errorMessage = (err as Error).message;
        if (errorMessage.includes('500') || errorMessage.includes('Panic')) {
          setError('Video-Chat ist momentan nicht verfügbar. Das Video kann trotzdem normal angeschaut werden.');
        } else {
          setError('Fehler beim Initialisieren des Video-Chats');
        }
      } else {
        setError('Fehler beim Initialisieren des Video-Chats');
      }
    } finally {
      setLoading(false);
    }
  };

  // Enhanced polling for new messages (improved from ChatWindow)
  useEffect(() => {
    if (chatSession === null || chatSession.id === '' || chatStarted === false) {
      return;
    }

    const fetchNewMessages = async (afterMessageId?: string) => {
      try {
        const messagesData = await VideoChatAPI.listMessages(chatSession.id, afterMessageId, false, debugMode);
        const newMessages = messagesData.messages;


        // Update chat session state
        if (messagesData.chatSession) {
          setChatSession(prev => {
            if (prev?.llmActive !== messagesData.chatSession?.llmActive ||
                prev?.closed !== messagesData.chatSession?.closed ||
                prev?.title !== messagesData.chatSession?.title) {
              return messagesData.chatSession ?? null;
            }
            return prev;
          });
        } else {
          // If backend doesn't provide session, check if we should disable llmActive
          // based on new assistant messages
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage?.role === 'assistant' && chatSession?.llmActive) {
            setChatSession(prev => prev ? { ...prev, llmActive: false } : prev);
          }
        }

        setMessages(prev => {
          const normalized = normalizeMessages(newMessages);
          if (afterMessageId !== undefined && normalized.length === prev.length) {
            return prev;
          }
          return normalized;
        });
      } catch (err) {
        if (isSessionNotFoundError(err)) {
          resetInvalidSessionState(true);
          return;
        }
        console.error('Polling error:', err);
      }
    };

    const getPollingInterval = () => {
      // Increase polling rate when LLM is active
      return chatSession?.llmActive ? 1000 : 3000; // 1 second when active, 3 seconds otherwise
    };

    const interval = setInterval(() => {
      const currentMessages = messagesRef.current;
      const lastMessage = currentMessages[currentMessages.length - 1];

      // For video chat, we should poll all messages initially if we don't have any
      if (currentMessages.length === 0) {
        fetchNewMessages(); // No afterMessageId to get all messages
      } else {
        fetchNewMessages(lastMessage?.id);
      }
    }, getPollingInterval());

    pollingIntervalRef.current = interval;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [chatSession?.id, chatSession?.llmActive, chatStarted, debugMode, resetInvalidSessionState]); // Include debugMode to update when it changes

  // Removed second useEffect - it was causing duplicate polling
  // The first useEffect handles all polling with dynamic intervals

  // Auto-disable llmActive when assistant response is received
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === 'assistant' && chatSession?.llmActive) {
      // Small delay to ensure the message is fully rendered
      setTimeout(() => {
        setChatSession(prev => prev ? { ...prev, llmActive: false } : prev);
      }, 500);
    }
  }, [messages, chatSession?.llmActive]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, chatSession?.llmActive]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent, llmLevel: LLMLevel = 'FULL', allowTools: boolean = true) => {
    e.preventDefault();

    if (!input.trim() || sending || !chatSession?.id || chatSession.llmActive) return;

    const messageContent = input.trim();
    setInput('');
    setSending(true);
    setError(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    try {
      const response = await VideoChatAPI.sendMessage(chatSession.id, messageContent, undefined, llmLevel, allowTools);

      if (response.userMessage && response.userMessage.id) {
        setMessages(prev => {
          // Check if message already exists to prevent duplicates
          const existingIds = new Set(prev.map(m => m?.id).filter(Boolean));
          if (!existingIds.has(response.userMessage.id)) {
            return [...prev, response.userMessage];
          }
          return prev;
        });
      }

      if (response.chatSession) {
        setChatSession(response.chatSession);
      }

      // If backend doesn't return userMessage or chatSession, immediately poll for new messages
      if (!response.userMessage || !response.chatSession) {
        try {
          const loadedSession = await loadMessagesForSession(chatSession, false, videoId, debugMode);
          if (loadedSession !== null) {
            setMessages(loadedSession.messages);
            setChatSession(loadedSession.session);
          }
        } catch (pollErr) {
          console.error('Immediate polling failed:', pollErr);
        }
      }

      // If still no llmActive status, assume LLM is processing user messages
      // Check if the last message we know about is a user message without a following assistant response
      setTimeout(() => {
        const currentMessages = messagesRef.current;
        const lastMessage = currentMessages[currentMessages.length - 1];

        if (lastMessage?.role === 'user' && !chatSession.llmActive && !response.chatSession?.llmActive) {
          setChatSession(prev => prev ? { ...prev, llmActive: true } : prev);
        }
      }, 100); // Small delay to ensure messages state is updated

      // Backend handles session management automatically

      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    } catch (err) {
      setError('Es gab einen Fehler bei der Kommunikation mit der KI. Bitte versuche es erneut.');
      console.error('Chat error:', err);
    } finally {
      setSending(false);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current !== null) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e, 'FULL', true);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  // Determine button text based on session state
  const getButtonText = () => {
    if (!chatSession) {
      return 'Chat starten';
    }

    if (chatSession.closed) {
      return 'Neuen Chat starten';
    }

    if (messages.length > 0) {
      return 'Chat fortsetzen';
    }

    return 'Chat starten';
  };

  // Show start button if chat hasn't been started yet
  if (!chatStarted) {
    return (
      <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faRobot} className="w-5 h-5 text-blue-600" />
            KI-Assistent
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {loading ? 'Lade bestehenden Chat...' : 'Starte einen Chat um Fragen zum Video zu stellen'}
          </p>
        </div>
        <div className="flex-1 flex items-center justify-center min-h-0">
          <div className="text-center">
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faSpinner} className="w-5 h-5 animate-spin" />
                <span>Lade Chat...</span>
              </div>
            ) : (
              <button
                onClick={initializeVideoChat}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center gap-2 cursor-pointer"
              >
                <FontAwesomeIcon icon={faRobot} className="w-5 h-5" />
                {getButtonText()}
              </button>
            )}
            {error && (
              <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full bg-white dark:bg-gray-800 rounded-xl shadow-lg">
      <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faRobot} className="w-5 h-5 text-blue-600" />
              KI-Assistent
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Fragen zur Lektion stellen...
            </p>
          </div>

          {/* Debug Button - only visible to admins */}
          {isAdmin && (
            <button
              onClick={() => setDebugMode(!debugMode)}
              disabled={isRefreshingDebug}
              className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded transition-colors cursor-pointer ${
                debugMode
                  ? 'text-white bg-orange-600 hover:bg-orange-700'
                  : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
              } ${isRefreshingDebug ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={debugMode ? 'Debug Mode deaktivieren' : 'Debug Mode aktivieren'}
            >
              <FontAwesomeIcon
                icon={isRefreshingDebug ? faSpinner : faBug}
                className={`w-3 h-3 mr-1 ${isRefreshingDebug ? 'animate-spin' : ''}`}
              />
              DEBUG
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {normalizeMessages(messages).map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              debugMode={debugMode}
            />
          ))}

        {chatSession?.llmActive && (
          <GeneratingResponseBubble />
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex-shrink-0 p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyDown}
            placeholder="Stelle eine Frage zum Video..."
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={1}
            disabled={sending || chatSession?.llmActive}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={(e) => handleSubmit(e, 'NANO', false)}
              disabled={!input.trim() || sending || chatSession?.llmActive}
              className="px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Schnelle Antwort - Verwendet ein kleineres, schnelleres Modell für einfache Fragen"
            >
              {sending ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faBolt} className="w-4 h-4" />
              )}
            </button>
            <button
              type="submit"
              disabled={!input.trim() || sending || chatSession?.llmActive}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              title="Normale Antwort (mit Tools)"
            >
              {sending ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default VideoChatPanel;
